'use strict';

/**
 * Shared plumbing for the in-card activity flows: the CSRF field, the
 * post-and-swap round trip, and the state animation. The underscore prefix
 * keeps the builder from emitting this shared module as its own standalone
 * bundle.
 */

var toast = require('core/components/toast');

/**
 * How long the outgoing fragment is given to fade before it is replaced,
 * and how long the card then takes to resize to the incoming one. Both must
 * stay in step with the --leaving and --resizing rules in
 * scss/components/_loyaltyActivities.scss.
 */
var LEAVE_DURATION = 140;
var RESIZE_DURATION = 220;

/**
 * @returns {boolean} - True when the visitor asked for reduced motion
 */
function prefersReducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
}

/**
 * Replaces a card's contents, fading the outgoing fragment and gliding the
 * card between the two heights — swapping in a single frame resizes the card
 * abruptly, which reads as a glitch mid-fade. The resting height is measured
 * by clearing the inline height rather than forcing `auto`, because the card
 * may be stretched by its grid item. Under a reduced-motion preference the
 * swap is immediate rather than merely still, so the step never feels like
 * lag.
 *
 * @param {jQuery} $card - The card whose contents are being replaced
 * @param {string} html - The fragment to swap in
 * @param {Function} [done] - Runs once the new fragment is in the DOM
 */
function swapCard($card, html, done) {
    if (prefersReducedMotion()) {
        $card.html(html);
        if (done) {
            done();
        }
        return;
    }

    var startHeight = $card.outerHeight();

    $card.addClass('loyalty-activity-card--leaving');

    setTimeout(function () {
        $card.removeClass('loyalty-activity-card--leaving').html(html);

        $card.css('height', '');
        var endHeight = $card.outerHeight();

        if (endHeight === startHeight) {
            if (done) {
                done();
            }
            return;
        }

        $card.css('height', startHeight + 'px');
        // Flush the start height so the transition has something to run
        // from; without this the browser coalesces both writes.
        $card[0].getBoundingClientRect();

        $card.addClass('loyalty-activity-card--resizing').css('height', endHeight + 'px');

        setTimeout(function () {
            $card.removeClass('loyalty-activity-card--resizing').css('height', '');
            if (done) {
                done();
            }
        }, RESIZE_DURATION);
    }, LEAVE_DURATION);
}

/**
 * The card skeleton ships the localized flow-error copy in a data
 * attribute (attributes on the card root survive fragment swaps); the
 * literal is the last-resort fallback for a detached context.
 *
 * @param {jQuery} $card - The card root
 * @returns {string} - The member-facing generic error copy
 */
function genericError($card) {
    return ($card && $card.attr('data-flow-error'))
        || 'We could not complete your request. Please try again.';
}

/**
 * Antavo-sourced messages are HTML-encoded before they reach the toast, which
 * string-concatenates its markup.
 *
 * @param {string} text - Untrusted message text
 * @returns {string} - The HTML-encoded text
 */
function escapeHtml(text) {
    return $('<div/>').text(text).html();
}

module.exports = {
    escapeHtml: escapeHtml,

    /**
     * Adds the grid's CSRF token to a POST body. The token is rendered into
     * the grid fragment by a remote include, so it is never baked into the
     * page-cached shell; it is read from the DOM because the fragment is the
     * only thing that knows the current token, and a page may carry more than
     * one grid.
     *
     * @param {jQuery} $card - The card the flow is running in
     * @param {Object} data - The POST body fields
     * @returns {Object} - The body with the token field added
     */
    withCsrf: function ($card, data) {
        var $token = $card.closest('.js-loyalty-activity-grid').find('.js-loyalty-activity-csrf');
        if (!$token.length) {
            // A card rendered outside a grid should not happen, but fall back
            // to the page rather than posting a request the route will reject.
            $token = $('.js-loyalty-activity-csrf').first();
        }
        if ($token.length && $token.attr('name')) {
            data[$token.attr('name')] = $token.val();
        }
        return data;
    },

    /**
     * Posts a flow action and swaps the card to the returned fragment. An
     * app-level error ({error: true}) or a transport error keeps the
     * current fragment, re-enables the triggering control, and shows a
     * toast.
     *
     * @param {Object} options - {url, data, $card, $restore, onSwapped}
     *   where $restore is re-enabled on error and onSwapped(data) runs
     *   after a successful swap
     */
    post: function (options) {
        $.spinner().start();
        $.ajax({
            url: options.url,
            type: 'POST',
            dataType: 'json',
            data: options.data,
            success: function (data) {
                $.spinner().stop();
                if (!data || data.error || !data.html) {
                    if (options.$restore) {
                        options.$restore.prop('disabled', false);
                    }
                    // The route decides what the member should read (Antavo's
                    // wording vs. authored copy) and sends it as data.message;
                    // this only needs a fallback for a response with no body.
                    // The card deliberately stays on its current fragment, so
                    // the toast is the only place the error appears.
                    toast.methods.show('danger', escapeHtml((data && data.message) || genericError(options.$card)));
                    return;
                }
                swapCard(options.$card, data.html, function () {
                    if (options.onSwapped) {
                        options.onSwapped(data);
                    }
                });
            },
            error: function () {
                $.spinner().stop();
                if (options.$restore) {
                    options.$restore.prop('disabled', false);
                }
                toast.methods.show('danger', escapeHtml(genericError(options.$card)));
            }
        });
    }
};
